import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, User } from '../../hooks/useAuth';
import { Lock, Mail, AlertCircle } from 'lucide-react';
import useTheme from '../../hooks/useTheme';
import ThemeToggle from '../../components/MasterAdmin/ui/ThemeToggle';
import {
  ADMIN_FCM_LAST_STATUS_KEY,
  registerAdminFcmForLoggedInUser,
} from '../../utils/adminFcm';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<User['role']>('MASTER_ADMIN');
  const [operatorLabel, setOperatorLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  useTheme(); // We only need the hook for theme toggle functionality

  // Remove this useEffect:
  // useEffect(() => {
  //   if (isAuthenticated && !isLoading) {
  //     navigate('/');
  //   }
  // }, [isAuthenticated, isLoading, navigate]);

  if (isAuthenticated && !isLoading) {
    if (user?.role === 'MASTER_ADMIN') {
      navigate('/', { replace: true });
      return null;
    } else if (user?.role === 'SALES_EXECUTIVE') {
      navigate('/sales/dashboard', { replace: true });
      return null;
    } else if (user?.role === 'ACCOUNTANT') {
      navigate('/accountant/dashboard', { replace: true });
      return null;
    } else if (user?.role === 'WRITER') {
      navigate('/writer/dashboard', { replace: true });
      return null;
    } else if (user?.role === 'MARKETING') {
      navigate('/marketing/dashboard', { replace: true });
      return null;
    } else {
      navigate('/', { replace: true });
      return null;
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      console.log('[LoginPage] Attempting login', { email, role, operatorLabel });
      if (role === 'WRITER' && !operatorLabel.trim()) {
        setError('Enter your name (operator label) so shared writer logins can be tracked.');
        setLoading(false);
        return;
      }
      const success = await login(email, password, role, {
        operatorLabel: operatorLabel.trim() || undefined,
      });
      if (success) {
        console.log('[LoginPage] Login success, calling FCM registration');
        try {
          await registerAdminFcmForLoggedInUser();
          console.log('[LoginPage] FCM registration success');
        } catch (fcmErr) {
          console.warn('[LoginPage] FCM registration failed', fcmErr);
          const statusMsg = localStorage.getItem(ADMIN_FCM_LAST_STATUS_KEY) || 'Unknown FCM error';
          console.warn('[LoginPage] FCM status:', statusMsg);
        }
        // Use the authenticated user's role for navigation
        if (user?.role === 'MASTER_ADMIN') {
          navigate('/', { replace: true });
        } else if (user?.role === 'SALES_EXECUTIVE') {
          navigate('/sales/dashboard', { replace: true });
        } else if (user?.role === 'ACCOUNTANT') {
          navigate('/accountant/dashboard', { replace: true });
        } else if (user?.role === 'WRITER') {
          navigate('/writer/dashboard', { replace: true });
        } else if (user?.role === 'MARKETING') {
          navigate('/marketing/dashboard', { replace: true });
        } else {
          // You can add navigation for other roles here if needed
          navigate('/', { replace: true });
        }
      } else {
        setError('Invalid email or password. Please check your credentials and try again.');
      }
    } catch (err) {
      setError('Server error. Please try again later or contact support.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin h-12 w-12 rounded-full border-4 border-primary-500 border-t-transparent"></div>
          <p className="text-gray-600 dark:text-gray-400">Logging in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-200">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      
      <div className="flex flex-1 items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="mx-auto h-16 w-16 bg-primary-600 dark:bg-primary-500 flex items-center justify-center rounded-full mb-4">
              <span className="text-white text-2xl font-bold">VM</span>
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">Vedika Master Admin</h2>
            <p className="mt-2 text-base text-gray-600 dark:text-gray-400">
              Sign in to your admin dashboard
            </p>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden transition-all">
            <div className="px-6 py-8 sm:px-10">
              {error && (
                <div className="mb-6 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded-lg flex items-start animate-fade-in">
                  <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              
              <form className="space-y-6" onSubmit={handleLogin}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email address
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full pl-10 py-3 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => alert('Forgot password functionality coming soon!')}
                      className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full pl-10 py-3 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="Enter your password"
                    />
                  </div>
                </div>

                {/* Role selection dropdown */}
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Role
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={role}
                    onChange={e => setRole(e.target.value as any)}
                    className="mt-1 block w-full py-3 pl-3 pr-10 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="MASTER_ADMIN">Master Admin</option>
                    <option value="SALES_EXECUTIVE">Sales Executive</option>
                    <option value="ACCOUNTANT">Accountant</option>
                    <option value="WRITER">Writer</option>
                    <option value="MARKETING">Marketing</option>
                    <option value="SUPPORT">Support</option>
                    <option value="TECH">Tech</option>
                    <option value="HR">HR</option>
                  </select>
                </div>

                {(role === 'WRITER' || operatorLabel.length > 0) && (
                  <div>
                    <label htmlFor="operatorLabel" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Your name {role === 'WRITER' ? '(required for shared logins)' : '(optional)'}
                    </label>
                    <input
                      id="operatorLabel"
                      name="operatorLabel"
                      type="text"
                      value={operatorLabel}
                      onChange={(e) => setOperatorLabel(e.target.value)}
                      className="mt-1 block w-full py-3 px-3 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="e.g. Kusum, A, or B"
                    />
                    {role === 'WRITER' && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        If multiple people share this writer account, each person must enter their own name so activity can be separated by device + operator.
                      </p>
                    )}
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Remember me
                  </label>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {loading ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Signing in...</span>
                      </div>
                    ) : 'Sign in'}
                  </button>
                </div>
              </form>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-750 dark:bg-opacity-40 border-t border-gray-100 dark:border-gray-700 sm:px-10">
              <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                By signing in, you agree to Vedika's Terms of Service and Privacy Policy
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-8 pb-4">
        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          &copy; {new Date().getFullYear()} Vedika Master Admin. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage; 